/**
 * STU-10-FR-05 / MC-08 — the reduced-media way into a live class.
 *
 * A camera or microphone that is denied, missing or busy must not keep a
 * learner out of the room. Joining and being counted are the provider's
 * join/leave facts on the server (MC08-R01: "Apagar la cámara no borra ni
 * invalida los registros de conexión"); the devices only decide what the
 * others see and hear. So each device is started on its own, a failure is
 * recorded rather than thrown, and the caller joins whatever happened.
 *
 * Kept free of the Chime SDK types so it can be tested without a browser.
 */

/** The slice of the Chime `AudioVideoFacade` this needs. */
export interface LocalMediaControls {
  listAudioInputDevices(): Promise<Array<{ deviceId: string }>>;
  startAudioInput(deviceId: string): Promise<unknown>;
  listVideoInputDevices(): Promise<Array<{ deviceId: string }>>;
  startVideoInput(deviceId: string): Promise<unknown>;
}

/**
 * `ON` — started. `OFF` — the learner chose to enter without it.
 * `UNAVAILABLE` — denied by the browser, missing, or failed to start.
 */
export type DeviceState = 'ON' | 'OFF' | 'UNAVAILABLE';

export interface LocalMedia {
  audio: DeviceState;
  video: DeviceState;
}

async function startOne(
  list: () => Promise<Array<{ deviceId: string }>>,
  start: (deviceId: string) => Promise<unknown>,
): Promise<DeviceState> {
  try {
    const devices = await list();
    if (!devices[0]) return 'UNAVAILABLE';
    await start(devices[0].deviceId);
    return 'ON';
  } catch {
    return 'UNAVAILABLE';
  }
}

/**
 * Start the microphone and, unless the learner chose otherwise, the camera.
 * Never throws for a device: the worst outcome is a learner who listens.
 */
export async function startLocalMedia(
  av: LocalMediaControls,
  opts: { camera: boolean } = { camera: true },
): Promise<LocalMedia> {
  const audio = await startOne(
    () => av.listAudioInputDevices(),
    (id) => av.startAudioInput(id),
  );
  const video = opts.camera
    ? await startOne(
        () => av.listVideoInputDevices(),
        (id) => av.startVideoInput(id),
      )
    : 'OFF';
  return { audio, video };
}

/** Turn the camera on later, from the room. Same rule: a failure is a state, not an error. */
export function startCamera(av: LocalMediaControls): Promise<DeviceState> {
  return startOne(
    () => av.listVideoInputDevices(),
    (id) => av.startVideoInput(id),
  );
}

/** What the learner is told when something is off. Null when both are on. */
export function reducedMediaNotice(media: LocalMedia): string | null {
  const noCamera = media.video !== 'ON';
  const noMic = media.audio !== 'ON';
  if (!noCamera && !noMic) return null;
  const what =
    noCamera && noMic ? 'sin cámara ni micrófono' : noCamera ? 'sin cámara' : 'sin micrófono';
  const fix =
    media.video === 'UNAVAILABLE' || media.audio === 'UNAVAILABLE'
      ? ' Si quieres usarlos, revisa los permisos del navegador y vuelve a entrar.'
      : '';
  return `Estás en la clase ${what}. Igual cuenta que estás en clase: no hace falta la cámara.${fix}`;
}
